import React, { useState } from 'react';
// Importar hooks de navegación de React Router
import { useNavigate } from 'react-router-dom';
// Importar manejador de formularios react-hook-form para validación sencilla
import { useForm } from 'react-hook-form';
// Importar notificaciones interactivas toast
import { toast } from 'react-hot-toast';
// Importar los endpoints de participantes desde el servicio de API
import { participantApi } from '../services/api';
// Importar iconos desde heroicons para darle una estética premium
import { UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

function Login() {
  // Hook de navegación de React Router para redirigir tras loguearse
  const navigate = useNavigate();
  // Estado local para manejar el spinner del botón de envío
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inicializar hooks de react-hook-form con validación y desestructurar campos
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Función manejadora al enviar el formulario de login
  const onSubmit = async (data) => {
    // Activar spinner e inhabilitar botón de envío
    setIsSubmitting(true);
    try {
      const response = await participantApi.login(data.email, data.password);
      // Mostrar toast de éxito con el nombre del usuario
      toast.success(`¡Bienvenido de vuelta, ${response.data.name}!`);
      
      // Guardar el participante en el localStorage para mantener la sesión activa de forma consistente
      localStorage.setItem('activeParticipant', JSON.stringify(response.data.participant));
      localStorage.setItem('authToken', response.data.token);
      
      // Limpiar los campos del formulario
      reset();
      
      // Redirigir al usuario de inmediato a su Dashboard
      navigate('/dashboard');
    } catch (error) {
      // Manejar errores de credenciales o de red mostrando una alerta descriptiva
      toast.error(error.response?.data?.error || 'No se pudo iniciar sesión. Verifica tu conexión.');
    } finally {
      // Desactivar spinner de carga
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Encabezado Principal del Login */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Iniciar Sesión
        </h1>
        <p className="text-gray-600">
          Ingresa tus credenciales para acceder a tus tests y progreso
        </p>
      </div>

      {/* Contenedor del Formulario con Estilo Premium y Sombras Suaves */}
      <div className="bg-white rounded-lg card-shadow p-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Campo Correo Electrónico */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Correo Electrónico *
            </label>
            <div className="relative rounded-md shadow-sm">
              {/* Icono de sobre colocado a la izquierda del input */}
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="email"
                id="email"
                {...register('email', {
                  required: 'El correo electrónico es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Formato de correo electrónico inválido'
                  }
                })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                placeholder="juan.perez@example.com"
              />
            </div>
            {/* Mensajes de error de validación */}
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña *
            </label>
            <input
              type="password"
              id="password"
              {...register('password', { required: 'La contraseña es requerida' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              autoComplete="current-password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          {/* Botón de Envío Interactivo con Micro-animación de carga */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  {/* Spinner SVG animado para indicar carga */}
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Iniciando sesión...
                </span>
              ) : (
                'Ingresar al Sistema'
              )}
            </button>
          </div>
        </form>

        {/* Sección de Registro en caso de no poseer una cuenta */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            ¿Aún no tienes una cuenta registrada?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-indigo-600 hover:text-indigo-500 font-medium transition-all"
            >
              Regístrate aquí
            </button>
          </p>
        </div>
      </div>

      {/* Sección Informativa con Tarjeta con Gradiente Suave */}
      <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-100">
        <h3 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center">
          <UserIcon className="h-5 w-5 mr-2 text-indigo-600" />
          Acceso Simple y Consistente
        </h3>
        <p className="text-sm text-indigo-800 leading-relaxed">
          Para garantizar la consistencia en el seguimiento de tus rasgos de personalidad (MBTI), 
          fortalezas (VIA) y motivaciones (Eneagrama), tu información está vinculada a tu correo. 
          Al iniciar sesión, recuperamos tu historial de mediciones de manera íntegra.
        </p>
      </div>
    </div>
  );
}

export default Login;
